import { PagePartsFragmentFragment } from "~/types/pageParts"
import { usePublicProductFindManyQuery } from "~/gql/urql.generated"
import type { Product } from "~/types/type.generated"
import { getRecommendRequestBody } from "~/utils/functions/recommend"
import type { ApiRecommendProductsResponse } from "~/types/server/api/recommend/product"
import { PagePartsSetType } from "~/types/page"
import { PagePartsProductType } from "~/types/product"

export const useFetchProductForPageParts = () => {
  const buildProductIds = (pageParts: PagePartsFragmentFragment[]) => {
    const productIds: Set<string> = new Set()
    pageParts.forEach((pagePart) => {
      for (const productId of pagePart.product_ids ?? []) {
        productIds.add(productId)
      }
      if (pagePart.product_id) productIds.add(pagePart.product_id)
    })
    return Array.from(productIds)
  }

  const fetchProductFindMany = async (productIds: string[]) => {
    if (!productIds.length) return []

    const { executeQuery: executeQueryProductFindMany } = usePublicProductFindManyQuery({
      requestPolicy: "cache-first",
      variables: {
        filter: { _operators: { document_id: { in: productIds } } },
      },
      pause: true,
    })

    const { data } = await executeQueryProductFindMany()
    return (data.value?.publicProductFindMany ?? []) as Product[]
  }

  /**
   * 1.各ページパーツに紐づく商品情報を追加
   * 2.複数商品の場合はproduct_idsの順番に並び替える
   */
  const updatePagePartsWithProducts = (
    pageParts: PagePartsSetType[],
    products: Product[] = [],
    recommendProducts: PagePartsProductType[] = [],
  ): void => {
    pageParts.forEach((pageParts) => {
      if (products.length && pageParts.product_ids.length) {
        const findProducts: Product[] = []
        pageParts.product_ids.forEach((productId) => {
          const findProduct = products.find((product) => product.document_id === productId)
          if (findProduct) {
            findProducts.push(findProduct)
          }
        })
        pageParts.products = findProducts
      }
      if (recommendProducts.length && pageParts.detail.is_ai_recommend) {
        pageParts.recommendProducts = recommendProducts.slice(0, pageParts.detail.ai_recommend_number)
      }
      if (pageParts.product_id) {
        pageParts.product = products.find((product) => product.document_id === pageParts.product_id)
      }
    })
  }

  /**
   * productのみ設定する（商品詳細高速化のため）
   */
  const updatePagePartsWithProductOnly = (pageParts: PagePartsFragmentFragment[], products: Product[]): void => {
    pageParts.forEach((pageParts) => {
      if (pageParts.product_id) {
        pageParts.product = products.find((product) => product.document_id === pageParts.product_id)
      }
    })
  }

  const fetchRecommendProducts = async (limit: number, view_product_id?: string) => {
    const resultProducts = await $fetch<ApiRecommendProductsResponse>("/api/recommend/product_detail", {
      method: "POST",
      body: { ...getRecommendRequestBody(limit), view_product_id },
    })
    return resultProducts?.products ?? []
  }

  const setProducts = async (pageParts: PagePartsSetType[], view_product_id?: string) => {
    const productIds = buildProductIds(pageParts)

    // レコメンドAPIから取得するパーツがあるかどうか
    let isAiRecommend = false
    let aiRecommendNumber = 0
    pageParts.forEach((pagePart) => {
      if (pagePart.detail.is_ai_recommend) {
        isAiRecommend = true
        if (pagePart.detail.ai_recommend_number && aiRecommendNumber < pagePart.detail.ai_recommend_number) {
          aiRecommendNumber = pagePart.detail.ai_recommend_number
        }
      }
    })

    const [products, recommendProducts] = await Promise.all([
      productIds.length ? fetchProductFindMany(productIds) : undefined,
      isAiRecommend ? fetchRecommendProducts(aiRecommendNumber, view_product_id) : undefined,
    ])
    if (products || recommendProducts) {
      updatePagePartsWithProducts(pageParts, products ?? [], recommendProducts ?? [])
    }
  }

  /**
   * タブグループ用パーツと親パーツを分ける
   */
  const createChildPagePartsList = (pagePartsList: PagePartsSetType[]) => {
    const tempPartsList: PagePartsSetType[] = []
    const tempChildPartsIds: string[] = []
    for (const pageParts of pagePartsList) {
      // タブグループ内のパーツは childPartsList に格納する
      if (pageParts.child_page_parts_ids && pageParts.child_page_parts_ids?.length > 0) {
        tempChildPartsIds.push(...pageParts.child_page_parts_ids)
        tempPartsList.push({
          ...pageParts,
          childPartsList: pagePartsList.filter((parts) => pageParts.child_page_parts_ids?.includes(parts.document_id)),
        })
      } else {
        tempPartsList.push(pageParts)
      }
    }
    return { tempPartsList, tempChildPartsIds }
  }

  return {
    buildProductIds,
    fetchProductFindMany,
    updatePagePartsWithProducts,
    updatePagePartsWithProductOnly,
    setProducts,
    createChildPagePartsList,
  }
}
